import { IconType } from 'components/atoms/Icon';
import { appSwitchRoutes } from 'constants/routes';
import { StageActionAvailabilityStatus } from 'enums/stageAction';
import {
  AiInterviewAssessmentStageActionCandidateStatus,
  CoderbyteAssessmentStageActionCandidateStatus,
  CognitiveKind,
  CognitiveTestAssessmentStageActionCandidateStatus,
  DeiStageActionCandidateStatus,
  EnglishProficiencyAssessmentStageActionCandidateStatus,
  FormCandidateStatus,
  MyRoleStageActionsQuery,
  PersonalityTestAssessmentStageActionCandidateStatus,
  RssAssessmentCandidateStatus,
  RssAssessmentType,
} from 'generated/graphql';
import { TFunction } from 'i18next';

type TMyRoleStageActions = NonNullable<
  MyRoleStageActionsQuery['myRole']['stages'][number]
>['actions'];

/** How clicking the card behaves:
 * - `test` — gated behind the candidate-information form check
 * - `aiInterview` — plain link (tracked), disabled once completed
 * - `default` — plain in-app navigation when available
 */
export type TRoleActionCardKind = 'test' | 'aiInterview' | 'default';

export type TRoleActionCardData = {
  key: string;
  title: string;
  icon: IconType;
  availability: StageActionAvailabilityStatus;
  linkTo: string;
  kind: TRoleActionCardKind;
  /** Analytics payload for AI interview cards. */
  aiInterview?: {
    actionCandidateId: string;
    conferenceId?: string;
  };
};

const availabilityFromCompleted = (
  completed: boolean
): StageActionAvailabilityStatus =>
  completed
    ? StageActionAvailabilityStatus.Completed
    : StageActionAvailabilityStatus.Available;

const simpleAvailability = (
  status:
    | PersonalityTestAssessmentStageActionCandidateStatus
    | CognitiveTestAssessmentStageActionCandidateStatus
    | EnglishProficiencyAssessmentStageActionCandidateStatus
    | FormCandidateStatus
): StageActionAvailabilityStatus =>
  availabilityFromCompleted(status === 'COMPLETED');

const cognitiveKindConfig: Record<
  CognitiveKind,
  { titleKey: string; icon: IconType }
> = {
  [CognitiveKind.Inductive]: {
    titleKey: 'common.testTypes.inductive',
    icon: 'inductive_test',
  },
  [CognitiveKind.Numerical]: {
    titleKey: 'common.testTypes.numerical',
    icon: 'numerical_test',
  },
  [CognitiveKind.Verbal]: {
    titleKey: 'common.testTypes.verbal',
    icon: 'verbal_test',
  },
};

/** Maps backend assessment names onto translation keys, mirroring the old
 * StageViewPresentational cardsTranslationConfig. Unknown names are shown
 * as-is — the Coderbyte action's name is admin-given and free to be anything. */
const assessmentTitleTranslationKey: Record<string, string> = {
  Coding: 'common.testTypes.coding',
};

/** The RSS card always points at the first not-yet-submitted assessment
 * (soft before technical), mirroring StageActionSkillsSelfAssessmentCard. */
const rssCardTarget = (
  actions: TMyRoleStageActions
): { id: number; type: RssAssessmentType } | null => {
  const soft = actions.softRssAssessmentAction;
  const technical = actions.technicalRssAssessmentAction;

  if (soft && soft.status !== RssAssessmentCandidateStatus.Submitted) {
    return { id: soft.id, type: RssAssessmentType.SoftSkill };
  }
  if (
    technical &&
    technical.status !== RssAssessmentCandidateStatus.Submitted
  ) {
    return { id: technical.id, type: RssAssessmentType.TechnicalSkill };
  }
  if (soft) {
    return { id: soft.id, type: RssAssessmentType.SoftSkill };
  }
  if (technical) {
    return { id: technical.id, type: RssAssessmentType.TechnicalSkill };
  }
  return null;
};

/**
 * Flattens the per-type action groups into the canonical display order:
 * personality, cognitive tests, the Coderbyte assessment, English proficiency,
 * skills self-assessment, DEI, form, AI interviews (mirrors the old
 * GetStageActions ordering).
 */
export const parseRoleActionCards = (
  actions: TMyRoleStageActions,
  t: TFunction,
  roleId: string,
  stageId: string
): TRoleActionCardData[] => {
  const rssStatuses = [
    actions.softRssAssessmentAction,
    actions.technicalRssAssessmentAction,
  ]
    .filter(Boolean)
    .map(rssAction => rssAction!.status);
  const rssTarget = rssCardTarget(actions);

  const cards: (TRoleActionCardData | null)[] = [
    !actions.personalityTestAction
      ? null
      : {
          key: `personality-${actions.personalityTestAction.id}`,
          title: t('common.testTypes.personality'),
          icon: 'personality_test',
          availability: simpleAvailability(
            actions.personalityTestAction.status
          ),
          linkTo: appSwitchRoutes.roleStagePersonalityTest.getUrl(
            roleId,
            stageId
          ),
          kind: 'test',
        },
    ...actions.cognitiveTestActions.map(
      (cognitiveTestAction): TRoleActionCardData => ({
        key: `cognitive-${cognitiveTestAction.id}`,
        title: t(cognitiveKindConfig[cognitiveTestAction.kind].titleKey),
        icon: cognitiveKindConfig[cognitiveTestAction.kind].icon,
        availability: simpleAvailability(cognitiveTestAction.status),
        linkTo: appSwitchRoutes.roleStageCognitiveTest.getUrl(
          roleId,
          stageId,
          cognitiveTestAction.id
        ),
        kind: 'test',
      })
    ),
    // At most one Coderbyte assessment per stage, so this is a single card
    // rather than the list its external-assessment predecessor produced.
    //
    // A completed sitting stays completed whether or not Coderbyte has returned
    // a score yet: grading is asynchronous, and the candidate has nothing left
    // to do either way.
    !actions.coderbyteAction
      ? null
      : {
          key: `coderbyte-${actions.coderbyteAction.id}`,
          title: assessmentTitleTranslationKey[actions.coderbyteAction.name]
            ? t(assessmentTitleTranslationKey[actions.coderbyteAction.name])
            : actions.coderbyteAction.name,
          icon: 'cog' as IconType,
          availability: availabilityFromCompleted(
            actions.coderbyteAction.status ===
              CoderbyteAssessmentStageActionCandidateStatus.Completed
          ),
          linkTo: appSwitchRoutes.roleStageCoderbyteAssessment.getUrl(
            roleId,
            stageId
          ),
          kind: 'default',
        },
    // The in-house English proficiency test. It sits next to the coding
    // assessment because that is where its Coderbyte-hosted predecessor
    // appeared.
    !actions.englishProficiencyAction
      ? null
      : {
          key: `english-proficiency-${actions.englishProficiencyAction.id}`,
          title: t('common.testTypes.englishProficiency'),
          icon: 'language',
          availability: simpleAvailability(
            actions.englishProficiencyAction.status
          ),
          linkTo: appSwitchRoutes.roleStageEnglishProficiencyTest.getUrl(
            roleId,
            stageId
          ),
          kind: 'test',
        },
    rssStatuses.length === 0 || !rssTarget
      ? null
      : {
          key: 'rss',
          title: t('common.testTypes.selfAssessment'),
          icon: 'skills_self_assessment',
          availability: availabilityFromCompleted(
            rssStatuses.every(
              status => status === RssAssessmentCandidateStatus.Submitted
            )
          ),
          linkTo: appSwitchRoutes.roleStageRssAssessment.getUrl(
            roleId,
            stageId,
            rssTarget.id,
            rssTarget.type
          ),
          kind: 'default',
        },
    !actions.deiAction
      ? null
      : {
          key: `dei-${actions.deiAction.id}`,
          title: t('common.testTypes.dei'),
          icon: 'dei',
          availability: availabilityFromCompleted(
            actions.deiAction.status ===
              DeiStageActionCandidateStatus.Submitted ||
              actions.deiAction.status ===
                DeiStageActionCandidateStatus.Declined
          ),
          linkTo: appSwitchRoutes.roleStageDeiIntro.getUrl(roleId, stageId),
          kind: 'default',
        },
    ...actions.formActions.map(
      (formAction): TRoleActionCardData => ({
        key: `form-${formAction.id}`,
        title: t('common.testTypes.form'),
        icon: 'questions' as IconType,
        availability: simpleAvailability(formAction.status),
        linkTo: appSwitchRoutes.roleStageForm.getUrl(
          roleId,
          stageId,
          formAction.formType
        ),
        kind: 'default',
      })
    ),
    ...actions.aiInterviewActions.map(
      (aiInterviewAction): TRoleActionCardData => ({
        key: `ai-interview-${aiInterviewAction.id}`,
        title: aiInterviewAction.name,
        icon: 'brain' as IconType,
        availability:
          aiInterviewAction.status ===
          AiInterviewAssessmentStageActionCandidateStatus.Completed
            ? StageActionAvailabilityStatus.Completed
            : aiInterviewAction.status ===
                AiInterviewAssessmentStageActionCandidateStatus.Failed
              ? StageActionAvailabilityStatus.NotAvailable
              : StageActionAvailabilityStatus.Available,
        linkTo: appSwitchRoutes.aiInterview.getUrl(
          roleId,
          stageId,
          aiInterviewAction.id
        ),
        kind: 'aiInterview',
        aiInterview: {
          actionCandidateId: aiInterviewAction.id,
          conferenceId: aiInterviewAction.conferenceId ?? undefined,
        },
      })
    ),
  ];

  return cards.filter(Boolean) as TRoleActionCardData[];
};
